import { useMemo, useState } from 'react';
import { ChevronDown, RotateCcw, Search, SlidersHorizontal } from 'lucide-react';
import type { LigneMatching, PropositionVue, StatutProposition } from '../../lib/useMatching';

/**
 * Filtres du croisement candidats × entreprises.
 *
 * Ils **trient ce que le backend a déjà calculé**, ils ne recalculent rien : le
 * score affiché reste celui de l'API, et aucun critère n'est réévalué ici. Deux
 * calculs pour un même couple finiraient par diverger, et c'est l'écran qui
 * aurait tort.
 *
 * Les quatre filtres principaux sont les quatre critères du barème, dans leur
 * ordre de poids — Métier, Formation, Disponibilité, Mobilité. Chacun filtre
 * sur l'**état de la donnée**, pas sur sa valeur : c'est ce qui permet de
 * retrouver en un geste les dossiers à compléter, qui sont précisément ceux que
 * le moteur ne peut plus favoriser.
 */

/** Un critère peut être mesuré, non renseigné, ou hors référentiel. */
export type EtatFiltre = 'tous' | 'MESURE' | 'NON_RENSEIGNE' | 'NON_EVALUABLE';

/** La disponibilité se scinde en plus selon l'origine de la donnée. */
export type DisponibiliteFiltre = 'tous' | 'DECLAREE' | 'DEDUITE' | 'NON_RENSEIGNE' | 'NON_EVALUABLE';

export interface EtatFiltresCroisement {
  recherche: string;
  metier: EtatFiltre;
  formation: EtatFiltre;
  disponibilite: DisponibiliteFiltre;
  mobilite: EtatFiltre;
  scoreMinimum: string;
  commune: string;
  formationCode: string;
  statutProposition: string;
}

export const FILTRES_VIDES: EtatFiltresCroisement = {
  recherche: '',
  metier: 'tous',
  formation: 'tous',
  disponibilite: 'tous',
  mobilite: 'tous',
  scoreMinimum: '',
  commune: '',
  formationCode: '',
  statutProposition: '',
};

const OPTIONS_ETAT: { valeur: EtatFiltre; libelle: string }[] = [
  { valeur: 'tous', libelle: 'Tous' },
  { valeur: 'MESURE', libelle: 'Mesuré' },
  { valeur: 'NON_RENSEIGNE', libelle: 'Non renseigné' },
  { valeur: 'NON_EVALUABLE', libelle: 'Non évaluable' },
];

const OPTIONS_DISPONIBILITE: { valeur: DisponibiliteFiltre; libelle: string }[] = [
  { valeur: 'tous', libelle: 'Toutes' },
  { valeur: 'DECLAREE', libelle: 'Déclarée' },
  { valeur: 'DEDUITE', libelle: 'Déduite de la date d’entrée' },
  { valeur: 'NON_RENSEIGNE', libelle: 'Non renseignée' },
  { valeur: 'NON_EVALUABLE', libelle: 'Non évaluable' },
];

const OPTIONS_STATUT: { valeur: string; libelle: string }[] = [
  { valeur: '', libelle: 'Tous états' },
  { valeur: 'AUCUN', libelle: 'Jamais proposée' },
  { valeur: 'PROPOSEE', libelle: 'Proposée' },
  { valeur: 'ACCEPTEE', libelle: 'Acceptée' },
  { valeur: 'REFUSEE', libelle: 'Refusée' },
  { valeur: 'EXPIREE', libelle: 'Expirée' },
];

/** Nom de la ligne de détail produite par chaque critère, côté moteur. */
const LIGNE_DETAIL = {
  metier: 'Metier',
  formation: 'Formation',
  disponibilite: 'Disponibilite',
  mobilite: 'Mobilite',
} as const;

/**
 * État d'un critère dans une proposition, avec repli sur les propositions
 * enregistrées avant l'introduction des états (`points === null` y valait
 * « non renseigné »).
 */
function etatDe(proposition: PropositionVue, ligne: string): EtatFiltre | null {
  const critere = proposition.criteres.find((entree) => entree.critere === ligne);
  if (!critere) return null;
  if (critere.etat) return critere.etat;
  return critere.points === null ? 'NON_RENSEIGNE' : 'MESURE';
}

function correspondEtat(proposition: PropositionVue, ligne: string, filtre: EtatFiltre): boolean {
  if (filtre === 'tous') return true;
  return etatDe(proposition, ligne) === filtre;
}

function correspondDisponibilite(
  proposition: PropositionVue,
  filtre: DisponibiliteFiltre,
): boolean {
  if (filtre === 'tous') return true;
  const critere = proposition.criteres.find(
    (entree) => entree.critere === LIGNE_DETAIL.disponibilite,
  );
  if (!critere) return false;
  if (filtre === 'DECLAREE' || filtre === 'DEDUITE') {
    // Une proposition antérieure aux sources n'affirme rien : la compter comme
    // déclarée ferait passer pour vérifié ce qui n'a pas été marqué.
    return etatDe(proposition, LIGNE_DETAIL.disponibilite) === 'MESURE' && critere.source === filtre;
  }
  return etatDe(proposition, LIGNE_DETAIL.disponibilite) === filtre;
}

/** Ces filtres portent sur une proposition, pas sur la ligne candidat. */
function filtresPropositionActifs(filtres: EtatFiltresCroisement): boolean {
  return (
    filtres.metier !== 'tous' ||
    filtres.formation !== 'tous' ||
    filtres.disponibilite !== 'tous' ||
    filtres.mobilite !== 'tous' ||
    filtres.scoreMinimum.trim() !== '' ||
    filtres.commune !== '' ||
    filtres.statutProposition !== ''
  );
}

export function nombreFiltresActifs(filtres: EtatFiltresCroisement): number {
  let total = 0;
  if (filtres.recherche.trim()) total += 1;
  if (filtres.metier !== 'tous') total += 1;
  if (filtres.formation !== 'tous') total += 1;
  if (filtres.disponibilite !== 'tous') total += 1;
  if (filtres.mobilite !== 'tous') total += 1;
  if (filtres.scoreMinimum.trim()) total += 1;
  if (filtres.commune) total += 1;
  if (filtres.formationCode) total += 1;
  if (filtres.statutProposition) total += 1;
  return total;
}

/** Filtres secondaires, ceux qui vivent sous le repli. */
function nombreFiltresSecondaires(filtres: EtatFiltresCroisement): number {
  let total = 0;
  if (filtres.scoreMinimum.trim()) total += 1;
  if (filtres.commune) total += 1;
  if (filtres.formationCode) total += 1;
  if (filtres.statutProposition) total += 1;
  return total;
}

/** Propositions d'une ligne qui passent les filtres, dans l'ordre reçu. */
export function propositionsRetenues(
  ligne: LigneMatching,
  filtres: EtatFiltresCroisement,
): PropositionVue[] {
  const scoreMinimum = Number.parseInt(filtres.scoreMinimum, 10);
  const seuil = Number.isFinite(scoreMinimum) ? scoreMinimum : null;

  return ligne.propositions.filter((proposition) => {
    if (!correspondEtat(proposition, LIGNE_DETAIL.metier, filtres.metier)) return false;
    if (!correspondEtat(proposition, LIGNE_DETAIL.formation, filtres.formation)) return false;
    if (!correspondEtat(proposition, LIGNE_DETAIL.mobilite, filtres.mobilite)) return false;
    if (!correspondDisponibilite(proposition, filtres.disponibilite)) return false;
    if (seuil !== null && proposition.score < seuil) return false;
    if (filtres.commune && (proposition.entrepriseVille ?? '') !== filtres.commune) return false;
    if (filtres.statutProposition) {
      const statut = proposition.statutEnregistre;
      if (filtres.statutProposition === 'AUCUN' ? statut !== null : statut !== filtres.statutProposition) {
        return false;
      }
    }
    return true;
  });
}

/**
 * Ligne retenue, et propositions retenues pour elle.
 *
 * Une ligne sans proposition — candidat déjà en immersion, ou antenne sans
 * besoin ouvert — reste visible tant qu'aucun filtre de proposition n'est posé.
 * Dès qu'on en pose un, la question porte sur les propositions : une ligne qui
 * n'en a aucune n'y répond pas.
 */
export function appliquerFiltres(
  lignes: LigneMatching[],
  filtres: EtatFiltresCroisement,
): { ligne: LigneMatching; propositions: PropositionVue[] }[] {
  const recherche = filtres.recherche.trim().toLowerCase();
  const surPropositions = filtresPropositionActifs(filtres);

  return lignes
    .map((ligne) => ({ ligne, propositions: propositionsRetenues(ligne, filtres) }))
    .filter(({ ligne, propositions }) => {
      if (recherche) {
        const surCandidat = ligne.candidat.toLowerCase().includes(recherche);
        const surEntreprise = propositions.some((proposition) =>
          proposition.entrepriseNom.toLowerCase().includes(recherche),
        );
        const surImmersion = ligne.immersion?.entreprise.toLowerCase().includes(recherche) ?? false;
        if (!surCandidat && !surEntreprise && !surImmersion) return false;
      }
      if (filtres.formationCode && (ligne.formation ?? '') !== filtres.formationCode) return false;
      if (surPropositions && propositions.length === 0) return false;
      return true;
    });
}

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy transition focus:border-codialis-sky focus:bg-white focus:outline-none';

function Champ({
  libelle,
  actif,
  children,
}: {
  libelle: string;
  actif: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span
        className={`text-[10px] font-bold uppercase tracking-wider ${
          actif ? 'text-codialis-sky' : 'text-codialis-gray'
        }`}
      >
        {libelle}
        {actif && <span className="ml-1 text-codialis-mint">●</span>}
      </span>
      {children}
    </label>
  );
}

export function FiltresCroisement({
  filtres,
  onFiltres,
  lignes,
  tri,
  onTri,
}: {
  filtres: EtatFiltresCroisement;
  onFiltres: (filtres: EtatFiltresCroisement) => void;
  /** Sert uniquement à construire les listes de communes et de formations. */
  lignes: LigneMatching[];
  tri: 'score' | 'candidat';
  onTri: (tri: 'score' | 'candidat') => void;
}): JSX.Element {
  const [secondairesOuverts, setSecondairesOuverts] = useState(false);
  const actifs = nombreFiltresActifs(filtres);
  const secondaires = nombreFiltresSecondaires(filtres);

  const communes = useMemo(() => {
    const valeurs = new Set<string>();
    for (const ligne of lignes) {
      for (const proposition of ligne.propositions) {
        if (proposition.entrepriseVille) valeurs.add(proposition.entrepriseVille);
      }
    }
    return [...valeurs].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [lignes]);

  const formations = useMemo(() => {
    const valeurs = new Set<string>();
    for (const ligne of lignes) if (ligne.formation) valeurs.add(ligne.formation);
    return [...valeurs].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [lignes]);

  const modifier = <Cle extends keyof EtatFiltresCroisement>(
    cle: Cle,
    valeur: EtatFiltresCroisement[Cle],
  ): void => onFiltres({ ...filtres, [cle]: valeur });

  return (
    <div className="border-b border-gray-100 bg-white p-4 md:px-6">
      {/* Recherche rapide et tri : la première ligne, toujours visible. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray"
            aria-hidden="true"
          />
          <input
            type="search"
            placeholder="Rechercher un candidat ou une entreprise…"
            aria-label="Rechercher un candidat ou une entreprise"
            className={`${CLASSE_CHAMP} pl-9`}
            value={filtres.recherche}
            onChange={(evenement) => modifier('recherche', evenement.target.value)}
          />
        </div>
        <select
          value={tri}
          onChange={(evenement) => onTri(evenement.target.value as 'score' | 'candidat')}
          aria-label="Ordre de tri"
          className={`${CLASSE_CHAMP} w-full font-semibold sm:w-auto`}
        >
          <option value="score">Trier par meilleur score</option>
          <option value="candidat">Trier par nom du candidat</option>
        </select>
      </div>

      {/* Les quatre critères du barème, dans leur ordre de poids. */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Champ libelle="Métier / Poste" actif={filtres.metier !== 'tous'}>
          <select
            value={filtres.metier}
            onChange={(evenement) => modifier('metier', evenement.target.value as EtatFiltre)}
            className={CLASSE_CHAMP}
          >
            {OPTIONS_ETAT.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </select>
        </Champ>
        <Champ libelle="Formation visée" actif={filtres.formation !== 'tous'}>
          <select
            value={filtres.formation}
            onChange={(evenement) => modifier('formation', evenement.target.value as EtatFiltre)}
            className={CLASSE_CHAMP}
          >
            {OPTIONS_ETAT.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </select>
        </Champ>
        <Champ libelle="Disponibilité" actif={filtres.disponibilite !== 'tous'}>
          <select
            value={filtres.disponibilite}
            onChange={(evenement) =>
              modifier('disponibilite', evenement.target.value as DisponibiliteFiltre)
            }
            className={CLASSE_CHAMP}
          >
            {OPTIONS_DISPONIBILITE.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </select>
        </Champ>
        <Champ libelle="Mobilité" actif={filtres.mobilite !== 'tous'}>
          <select
            value={filtres.mobilite}
            onChange={(evenement) => modifier('mobilite', evenement.target.value as EtatFiltre)}
            className={CLASSE_CHAMP}
          >
            {OPTIONS_ETAT.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </select>
        </Champ>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setSecondairesOuverts((ouvert) => !ouvert)}
          aria-expanded={secondairesOuverts}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-codialis-navy transition hover:border-codialis-sky hover:text-codialis-sky"
        >
          <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
          Plus de filtres
          {secondaires > 0 && (
            <span className="rounded-full bg-codialis-mint px-2 py-0.5 text-[10px] font-bold text-codialis-navy">
              {secondaires}
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 transition ${secondairesOuverts ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>

        {actifs > 0 && (
          <>
            <span className="text-xs font-semibold text-codialis-sky">
              {actifs} filtre{actifs > 1 ? 's' : ''} actif{actifs > 1 ? 's' : ''}
            </span>
            <button
              type="button"
              onClick={() => onFiltres(FILTRES_VIDES)}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl px-3 text-sm font-semibold text-codialis-gray transition hover:text-codialis-navy"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              Réinitialiser
            </button>
          </>
        )}
      </div>

      {secondairesOuverts && (
        <div className="mt-3 grid grid-cols-1 gap-3 rounded-xl bg-gray-50 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <Champ libelle="Score minimum" actif={filtres.scoreMinimum.trim() !== ''}>
            <input
              type="number"
              min={0}
              max={100}
              inputMode="numeric"
              placeholder="0 à 100"
              className={`${CLASSE_CHAMP} bg-white`}
              value={filtres.scoreMinimum}
              onChange={(evenement) => modifier('scoreMinimum', evenement.target.value)}
            />
          </Champ>
          <Champ libelle="Commune de l’entreprise" actif={filtres.commune !== ''}>
            <select
              value={filtres.commune}
              onChange={(evenement) => modifier('commune', evenement.target.value)}
              className={`${CLASSE_CHAMP} bg-white`}
            >
              <option value="">Toutes les communes</option>
              {communes.map((commune) => (
                <option key={commune} value={commune}>
                  {commune}
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="Formation du candidat" actif={filtres.formationCode !== ''}>
            <select
              value={filtres.formationCode}
              onChange={(evenement) => modifier('formationCode', evenement.target.value)}
              className={`${CLASSE_CHAMP} bg-white`}
            >
              <option value="">Toutes les formations</option>
              {formations.map((formation) => (
                <option key={formation} value={formation}>
                  {formation}
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="État de la proposition" actif={filtres.statutProposition !== ''}>
            <select
              value={filtres.statutProposition}
              onChange={(evenement) => modifier('statutProposition', evenement.target.value)}
              className={`${CLASSE_CHAMP} bg-white`}
            >
              {OPTIONS_STATUT.map((option) => (
                <option key={option.valeur} value={option.valeur}>
                  {option.libelle}
                </option>
              ))}
            </select>
          </Champ>
        </div>
      )}
    </div>
  );
}

export const LIBELLES_STATUT_PROPOSITION: Record<StatutProposition, { texte: string; ton: string }> =
  {
    PROPOSEE: { texte: 'Proposée', ton: 'info' },
    ACCEPTEE: { texte: 'Acceptée', ton: 'success' },
    REFUSEE: { texte: 'Refusée', ton: 'danger' },
    EXPIREE: { texte: 'Expirée', ton: 'neutral' },
  };
